// =============================================================================
// MeuDriver — StudentWallet
// Minha carteira — /aluno/carteira
//
// Saldo disponível, valor travado em aulas agendadas, recarga por Pix e
// extrato. Regras em src/hooks/useWallet.ts.
// =============================================================================

import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Wallet, Lock, Plus, ArrowDownLeft, ArrowUpRight, Info, QrCode, CreditCard, Gift } from 'lucide-react'
import { motion } from 'framer-motion'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  MAX_TOPUP, MIN_TOPUP, POINTS_PER_REAL, WALLET_TX_LABELS,
  useRedeemPointsToWallet, useWalletBalance, useWalletHeld, useWalletTopup, useWalletTransactions,
  type TopupMethod,
} from '@/hooks/useWallet'
import { useLoyaltyPoints } from '@/hooks/useLoyalty'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatCurrency } from '@/lib/utils'

const QUICK_AMOUNTS = [50, 100, 200, 500]

export default function StudentWallet() {
  const { data: balance, isLoading: loadingBalance } = useWalletBalance()
  const { data: held } = useWalletHeld()
  const { data: transactions, isLoading: loadingTx } = useWalletTransactions()
  const topup = useWalletTopup()
  const [amount, setAmount] = useState<string>('100')
  const [method, setMethod] = useState<TopupMethod>('pix')
  const { data: points = 0 } = useLoyaltyPoints()
  const redeem = useRedeemPointsToWallet()
  const redeemable = Math.floor(points / POINTS_PER_REAL) * POINTS_PER_REAL
  const [searchParams] = useSearchParams()
  const qc = useQueryClient()

  useEffect(() => {
    const status = searchParams.get('payment')
    if (status === 'success') {
      toast.success('Pagamento recebido! O saldo entra assim que for confirmado (no cartão, em instantes).')
      qc.invalidateQueries({ queryKey: ['wallet'] })
    } else if (status === 'cancelled' || status === 'expired') {
      toast.info('Pagamento não concluído. Nenhum valor foi cobrado.')
    }
  }, [searchParams, qc])

  const value = Number(amount.replace(',', '.'))
  const valid = Number.isFinite(value) && value >= MIN_TOPUP && value <= MAX_TOPUP

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
          <Wallet className="h-6 w-6 text-brand-600" />
          Minha carteira
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Coloque saldo uma vez e use com qualquer instrutor: aula avulsa, experimental ou pacote.
        </p>
      </motion.div>

      {/* Saldo */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-teal-50 p-5">
          <p className="text-sm text-gray-600">Saldo disponível</p>
          {loadingBalance
            ? <Skeleton className="h-9 w-32 mt-1" />
            : <p className="text-3xl font-extrabold text-gray-900 mt-1">{formatCurrency(balance ?? 0)}</p>}
        </div>
        <div className="rounded-2xl border border-border/60 bg-white p-5">
          <p className="text-sm text-gray-600 flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5" />
            Travado em aulas agendadas
          </p>
          <p className="text-3xl font-extrabold text-gray-900 mt-1">{formatCurrency(held ?? 0)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Vai para o instrutor quando a aula for dada; volta para o saldo se a aula for cancelada a tempo.
          </p>
        </div>
      </div>

      {/* Recarga */}
      <section className="rounded-2xl border border-border/60 bg-white p-5 space-y-4">
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <Plus className="h-4 w-4 text-brand-600" />
          Adicionar saldo
        </h2>
        <div className="flex flex-wrap gap-2">
          {QUICK_AMOUNTS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setAmount(String(v))}
              className={cn(
                'rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
                value === v ? 'border-primary bg-primary text-white' : 'border-gray-200 text-gray-700 hover:border-primary',
              )}
            >
              {formatCurrency(v)}
            </button>
          ))}
        </div>
        <div className="flex gap-2" role="radiogroup" aria-label="Forma de pagamento">
          {([['pix', 'Pix', QrCode], ['card', 'Cartão de crédito', CreditCard]] as const).map(([m, label, Icon]) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={method === m}
              onClick={() => setMethod(m)}
              className={cn(
                'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                method === m ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-700 hover:border-primary',
              )}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="space-y-1.5 sm:w-48">
            <Label htmlFor="topup-amount">Outro valor (R$)</Label>
            <Input
              id="topup-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <Button
            type="button"
            disabled={!valid || topup.isPending}
            onClick={() => topup.mutate({ amount: value, method })}
          >
            {topup.isPending
              ? (method === 'card' ? 'Abrindo pagamento...' : 'Gerando Pix...')
              : `Pagar ${valid ? formatCurrency(value) : ''} ${method === 'card' ? 'no cartão' : 'via Pix'}`}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          De {formatCurrency(MIN_TOPUP)} a {formatCurrency(MAX_TOPUP)} por recarga. O saldo não é
          reembolsável em dinheiro: ele só pode ser usado em aulas e pacotes.{' '}
          Cancelou uma aula com 2 horas ou mais de antecedência? O valor volta para cá.
        </p>
      </section>

      {/* Pontos de fidelidade */}
      <section className="rounded-2xl border border-border/60 bg-white p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Gift className="h-4 w-4 text-brand-600" />
            Pontos de fidelidade: {points}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Você ganha 10 pontos a cada aula concluída. {POINTS_PER_REAL} pontos = {formatCurrency(1)} de saldo.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={redeemable < POINTS_PER_REAL || redeem.isPending}
          onClick={() => redeem.mutate(redeemable)}
        >
          {redeem.isPending
            ? 'Trocando...'
            : redeemable >= POINTS_PER_REAL
              ? `Trocar ${redeemable} pontos por ${formatCurrency(redeemable / POINTS_PER_REAL)}`
              : `Faltam ${POINTS_PER_REAL - (points % POINTS_PER_REAL)} pontos`}
        </Button>
      </section>

      {/* Extrato */}
      <section>
        <h2 className="text-base font-bold text-gray-900 mb-4">Extrato</h2>
        {loadingTx ? (
          <div className="space-y-2">
            <Skeleton className="h-14 rounded-xl" />
            <Skeleton className="h-14 rounded-xl" />
          </div>
        ) : !transactions || transactions.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border bg-white/60 py-10 text-center text-sm text-muted-foreground">
            Nenhuma movimentação ainda.
          </p>
        ) : (
          <ul className="divide-y rounded-2xl border border-border/60 bg-white">
            {transactions.map((t) => {
              const entrada = t.amount > 0
              return (
                <li key={t.id} className="flex items-center gap-3 px-4 py-3">
                  <div className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                    entrada ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600',
                  )}>
                    {entrada ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {t.booking_id
                        ? <Link to={`/aluno/aulas/${t.booking_id}`} className="hover:underline">{WALLET_TX_LABELS[t.kind]}</Link>
                        : WALLET_TX_LABELS[t.kind]}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(parseISO(t.created_at), "d 'de' MMM 'de' yyyy, HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={cn('text-sm font-semibold', entrada ? 'text-green-700' : 'text-gray-900')}>
                      {entrada ? '+' : '−'}{formatCurrency(Math.abs(t.amount))}
                    </p>
                    <p className="text-xs text-muted-foreground">saldo {formatCurrency(t.balance_after)}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}
